<template>
  <component
    :is
    :to
    class="fp-button"
    :class="{
      '-primary': primary,
      '-small': small,
      '-disabled': disabled
    }"
    :aria-disabled="disabled"
    @click="handleClick"
  >
    <div
      v-if="pending"
      class="fp-button__spinner-wrapper"
    >
      <vue-spinner />
    </div>
    <div
      class="fp-button__content-wrapper"
      :class="{
        '-hidden': pending
      }"
    >
      <slot />
    </div>
  </component>
</template>

<script setup lang="ts">
import { VueSpinner } from 'vue3-spinners';
import { NuxtLink } from '#components';
import { useHaptics } from '~/composables/use-haptics';

const haptics = useHaptics();

const props = defineProps<{
  primary?: boolean
  small?: boolean
  to?: string
  is?: string
  pending?: boolean
  disabled?: boolean
  noHaptics?: boolean
}>();

const is = computed(() => {
  if (props.is) return props.is;
  if (!props.to) return 'button';
  return NuxtLink;
});

const hapt = () => {
  if (props.noHaptics) return;
  if (props.disabled) return haptics('error');
  if (props.primary) return haptics('success');
};

const handleClick = (event: MouseEvent) => {
  hapt();

  if (props.disabled) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
};
</script>

<style scoped lang="scss">
.fp-button {
  text-align: center;
  display: block;
  box-sizing: border-box;
  width: 100%;
  font: inherit;
  padding: 16px;
  border-radius: 12px;
  border: 2px solid var(--clr-text-primary);
  font-weight: 600;
  font-size: large;
  cursor: pointer;
  @include filled-shadow(4);
  text-decoration: none;
  position: relative;

  &.-primary {
    background: var(--clr-brand);
    color: var(--clr-text-primary);
  }

  &.-small {
    padding: 8px;
  }

  &.-disabled {
    color: var(--clr-text-secondary);
  }

  &:active {
    transform: translate(4px, 4px);
    box-shadow: none;
  }

  &:active:disabled {
    transform: none;
    @include filled-shadow(4);
  }
}

.fp-button__content-wrapper.-hidden {
  opacity: 0;
}

.fp-button__spinner-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
}
</style>
